import {
  FormFieldSchema,
  FormFieldType,
  TriggerCondition,
  TriggerType,
} from "@app/components/forms/types/schema";
import {Skeleton, defaultGradientColor, GradientColor, GradientType, GradientColorStop} from "@shortsworks/ui"
import { cn } from "@shortsworks/ui/lib/utils";

export const WidgetSettingsSchema: FormFieldSchema[] = [
  {
    type: FormFieldType.RadioGroup,
    id: "widgetShape",
    label: "위젯 모양",
    description: "위젯의 모양을 선택하세요.",
    behavior: {
      required: true,
    },
    hideIndicator: true,
    defaultValue: "story",
    appearance: {
      border: false,
    },
    optionLayout: "horizontal",
    options: [
      {
        label: (
          <div className="flex gap-1.5">
            <div className="w-10 aspect-[9/16] flex items-center">
              <Skeleton className="rounded-full w-10 h-10 bg-primary/30" />
            </div>
            <div className="w-10 aspect-[9/16] flex items-center">
              <Skeleton className="rounded-full w-10 h-10 bg-primary/30" />
            </div>
            <div className="w-10 aspect-[9/16] flex items-center">
              <Skeleton className="rounded-full w-10 h-10 bg-primary/30" />
            </div>
            <div className="w-10 aspect-[9/16] flex items-center">
              <Skeleton className="rounded-full w-10 h-10 bg-primary/30" />
            </div>
          </div>
        ),
        value: "story",
        description: <p className="p-2 text-center">인스타그램 스토리 모양</p>,
      },
      {
        label: (
          <div className="flex gap-1.5">
            <Skeleton className="w-10 aspect-[9/16] bg-primary/30" />
            <Skeleton className="w-10 aspect-[9/16] bg-primary/30" />
            <Skeleton className="w-10 aspect-[9/16] bg-primary/30" />
            <Skeleton className="w-10 aspect-[9/16] bg-primary/30" />
          </div>
        ),
        value: "shorts",
        description: <p className="p-2 text-center">유튜브 쇼츠 모양</p>,
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Select,
    id: "postingSort",
    label: "게시 순서",
    description: "게시 순서에 따라 위젯에 표시될 컨텐츠가 순서가 달라집니다.",
    placeholder: "순서 선택",
    layout: "horizontal",
    defaultValue: "latest",
    behavior: {
      required: true,
    },
    appearance: {
      border: false,
    },
    options: [
      {
        label: "최신순",
        value: "latest",
      },
      {
        label: "과거순",
        value: "oldest",
      },
      {
        label: "조회순",
        value: "views",
      },
      {
        label: "무작위",
        value: "random",
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Select,
    id: "preview",
    label: "미리보기 방식",
    description: "미리보기 방식을 선택하세요.",
    placeholder: "미리보기 방식 선택",
    layout: "horizontal",
    defaultValue: "focus",
    behavior: {
      required: true,
    },
    options: [
      {
        label: "포커스 모드",
        value: "focus",
        description: "마우스 호버 시 프리뷰가 재생됩니다.",
      },
      {
        label: "슬라이드 모드",
        value: "slider",
        description: "프리뷰가 하나씩 재생되면서 슬라이딩 되고 포커스됩니다.",
      },
    ],
  },
  {
    type: FormFieldType.Switch,
    id: "customizePreviewDuration",
    label: "미리보기 시간 설정",
    description: (
      <p>
        컨텐츠 미리 보기 시간을 설절할 수 있습니다.
        <br />
        기본값은 <b>비디오 7초</b>, <b>이미지 3초</b> 입니다.
      </p>
    ),
    defaultValue: false,
    appearance: {
      border: false,
    },
  },
  {
    type: FormFieldType.Group,
    id: "previewDuration",
    noneStyle: true,
    behavior: {
      triggers: [
        {
          type: TriggerType.Show,
          field: "customizePreviewDuration",
          value: true,
          condition: TriggerCondition.Equal,
        },
      ],
    },
    schemas: [
      {
        type: FormFieldType.Number,
        id: "videoPreviewDuration",
        label: "비디오 미리보기 시간",
        description: "설정한 시간(초) 만큼 비디오의 미리보기를 재생합니다.",
        layout: "horizontal",
        defaultValue: 7,
        min: 1,
        max: 60,
      },
      {
        type: FormFieldType.Number,
        id: "imagePreviewDuration",
        label: "이미지 미리보기 시간",
        description: "설정한 시간(초) 만큼 이미지의 미리보기를 재생합니다.",
        layout: "horizontal",
        defaultValue: 3,
        behavior: {
          required: true,
        },
        min: 1,
        max: 10,
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Switch,
    id: "unmuteVideoByDefault",
    label: "영상 재생 시 사운드 켜기",
    description: "영상 재생 시 자동으로 사운드를 켜거나 끌 수 있습니다.",
    appearance: {
      border: false,
    },
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Switch,
    id: "showPostingTitle",
    label: "포스팅 제목 표시",
    description: "스토리형 타입의 위젯 제목을 표시하거나 숨길 수 있습니다.",
    appearance: {
      border: false,
    },
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Select,
    id: "displayType",
    label: "자동 롤링",
    layout: "horizontal",
    description:
      "포스팅의 개수가 위젯 영역을 넘어서면 포스팅이 자동으로 돌아가며 보입니다.",
    appearance: {
      border: false,
    },
    options: [
      {
        label: "롤링",
        value: "rolling",
        description: "롤링 상태를 적용합니다.",
      },
      {
        label: "고정",
        value: "fixed",
        description: "고정 상태를 유지합니다.",
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Select,
    id: "readMarkResetCycle",
    label: "읽음 표시 재생성",
    description: "스토리형 타입의 위젯 제목을 표시하거나 숨길 수 있습니다.",
    placeholder: "선택",
    layout: "horizontal",
    defaultValue: "0",
    appearance: {
      border: false,
    },
    options: [
      {
        label: "읽은 상태 유지",
        value: "none",
        description: "컨텐츠를 보면 읽음 상태 테두리가 사라진 채로 유지됩니다.",
      },
      {
        label: "12시간",
        value: "12",
        description: "12시간 이후 읽음 상태 테두리가 다시 표시됩니다.",
      },
      {
        label: "24시간",
        value: "24",
        description: "24시간 이후 읽음 상태 테두리가 다시 표시됩니다.",
      },
      {
        label: "36시간",
        value: "26",
        description: "36시간 이후 읽음 상태 테두리가 다시 표시됩니다.",
      },
      {
        label: "48시간",
        value: "48",
        description: "48시간 이후 읽음 상태 테두리가 다시 표시됩니다.",
      },
      {
        label: "항상 표시",
        value: "0",
        description: "읽은 상태 테두리가 항상 표시됩니다.",
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Switch,
    id: "tagMinimized",
    label: "상품 태그 접기",
    description: "상품 태그를 아이콘 형태로 표시할 수 있습니다.",
    appearance: {
      border: false,
    },
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Switch,
    id: "useSharedLink",
    label: "공유 기능 사용",
    description: "컨텐츠를 공유할 수 있는 기능을 사용할 수 있습니다.",
    appearance: {
      border: false,
    },
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Switch,
    id: "useRestrictGuest",
    label: "비회원 포스팅뷰 제한",
    description: "비회원이 볼 수 있는 포스팅의 갯수를 제한합니다.",
    appearance: {
      border: false,
    },
  },
  {
    type: FormFieldType.Number,
    id: "restrictGuest",
    label: "하루 최대 포스팅 갯수",
    description: "N개의 포스팅을 볼 경우 제한되며 로그인을 요청합니다.",
    layout: "horizontal",
    placeholder: "10",
    appearance: {
      border: false,
    },
    behavior: {
      triggers: [
        {
          type: TriggerType.Show,
          field: "useRestrictGuest",
          value: true,
          condition: TriggerCondition.Equal,
        },
      ],
    },
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Switch,
    id: "hidePriceToGuest",
    label: "비회원 가격 정보 제한",
    description: "컨텐츠를 공유할 수 있는 기능을 사용할 수 있습니다.",
    appearance: {
      border: false,
    },
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.RadioGroup,
    id: "widgetSize",
    label: "위젯 크기 설정",
    description: "위젯의 크기를 설절할 수 있습니다",
    defaultValue: "basic",
    optionLayout: "horizontal",
    hideIndicator: true,
    options: [
      {
        value: "small",
        description: <p className="p-2 text-center">스몰</p>,
        label: (formValues: any) => {
          return (
            <div
              className={cn(
                "p-[16px] flex items-center justify-center",
                formValues.widgetType !== "story" && "aspect-[9/16]",
              )}
            >
              <Skeleton
                className={cn(
                  "w-[68px] bg-primary/30",
                  "transition-all",
                  formValues.widgetType === "story"
                    ? "rounded-full aspect-square"
                    : "aspect-[9/16]",
                )}
              />
            </div>
          );
        },
      },
      {
        value: "basic",
        description: <p className="p-2 text-center">기본</p>,
        label: (formValues: any) => {
          return (
            <div
              className={cn(
                "p-[5px] flex items-center justify-center",
                formValues.widgetType !== "story" && "aspect-[9/16]",
              )}
            >
              <Skeleton
                className={cn(
                  "w-[90px] bg-primary/30",
                  "transition-all",
                  formValues.widgetType === "story"
                    ? "rounded-full aspect-square"
                    : "aspect-[9/16]",
                )}
              />
            </div>
          );
        },
      },
      {
        value: "large",
        description: <p className="p-2 text-center">라지</p>,
        label: (formValues: any) => {
          return (
            <Skeleton
              className={cn(
                "w-[100px] bg-primary/30",
                "transition-all",
                formValues.widgetType === "story"
                  ? "rounded-full aspect-square"
                  : "aspect-[9/16]",
              )}
            />
          );
        },
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Group,
    id: "borderColor",
    noneStyle: true,
    label: "테두리 설정",
    description: "위젯의 테두리 색상을 설정할 수 있습니다.",
    schemas: [
      {
        type: FormFieldType.Color,
        id: "storyBorderColor",
        defaultValue: defaultGradientColor(),
        label: "스토리 모양 테두리 컬러 설정",
        appearance: {
          border: false,
        },
        extraContent: ({storyBorderColor, widgetSize}) => {
          // 그라디언트 값을 제대로 가져오지 못하는 경우 에러 발생할 수 있음
          const currentColor = GradientColor.new(storyBorderColor?.type, storyBorderColor?.colorStops, storyBorderColor?.angle).toCss();
          return (
            <div className="flex w-full h-full items-center justify-center">
              <div
                className={cn(
                  "flex items-center justify-center border-[3px] border-solid border-transparent",
                  widgetSize === "small" && "w-[68px]",
                  widgetSize === "basic" && "w-[90px]",
                  widgetSize === "large" && "w-[100px]",
                  "rounded-full aspect-square",
                  "transition-all",
                )}
                style={{
                  backgroundImage: `linear-gradient(#fff, #fff), ${currentColor}`,
                  backgroundOrigin: "border-box",
                  backgroundClip: "padding-box, border-box",
                }}
              >
                <Skeleton
                  className={cn(
                    "bg-primary/30",
                    widgetSize === "small" && "w-[59px]",
                    widgetSize === "basic" && "w-[82px]",
                    widgetSize === "large" && "w-[92px]",
                    "rounded-full aspect-square",
                    "transition-all",
                  )}
                />
              </div>
            </div>
          );
        },
      },
      {
        type: FormFieldType.Switch,
        id: "shortsVisibleBorder",
        label: "쇼츠 모양 테두리 컬러 사용",
        description:
          "쇼츠 모양에서도 스토리 모양과 같이 테두리 컬러를 표시합니다.",
      },
      {
        type: FormFieldType.Switch,
        id: "useStoryBorderColor",
        label: "쇼츠 모양 테두리 컬러 변경",
        description:
          "쇼츠 모양에서 스토리 테두리 컬러와 다른 컬러를 지정합니다.",
      },
      {
        type: FormFieldType.Color,
        defaultValue: defaultGradientColor(),
        id: "shortsBorderColor",
        label: "쇼츠 모양 테두리 컬러 설정",
        appearance: {
          border: false,
        },
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useStoryBorderColor",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
        extraContent: ({ shortsBorderColor, widgetSize }: any) => {
          const currentColor = GradientColor.new(
            shortsBorderColor?.type,
            shortsBorderColor?.colorStops,
            shortsBorderColor?.angle,
          ).toCss();

          return (
            <div className="flex w-full h-full items-center justify-center">
              <div
                className={cn(
                  "flex items-center justify-center border-[3px] border-solid border-transparent",
                  widgetSize === "small" && "w-[130px]",
                  widgetSize === "basic" && "w-[170px]",
                  widgetSize === "large" && "w-[210px]",
                  "rounded-md aspect-[9/16]",
                  "transition-all",
                )}
                style={{
                  backgroundImage: `linear-gradient(#fff, #fff), ${currentColor}`,
                  backgroundOrigin: "border-box",
                  backgroundClip: "content-box, border-box",
                }}
              >
                <Skeleton
                  className={cn(
                    "bg-primary/30",
                    widgetSize === "small" && "w-[120px]",
                    widgetSize === "basic" && "w-[160px]",
                    widgetSize === "large" && "w-[200px]",
                    "aspect-[9/16]",
                    "transition-all",
                  )}
                />
              </div>
            </div>
          );
        },
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Group,
    id: "conversionCodeGroup",
    label: "전환 코드",
    noneStyle: true,
    schemas: [
      {
        type: FormFieldType.Switch,
        id: "useConversionCode",
        label: "전환 코드 사용하기",
        layout: "horizontal",
      },
      // required 관련 문제 수정 필요
      {
        type: FormFieldType.Text,
        id: "conversionCode",
        label: "전환 추적 코드",
        // horizontal 레이어가 따로 없는 점 확인 필요
        layout: "horizontal",
        // todo: 플랫폼 마다 다르도록 변경
        description:
          "카페24 통계 페이지에서 식별할 수 있는 추적 코드를[mo] 문자나 숫자의 형태로 넣어주세요. 예) shortform",
        // '플렉스지 {상품}-{상품채널_추적코드} 메뉴에서[mo] 추가한 상품 채널의 숫자를 넣어주세요. 예) 30'
        placeholder: "전환 코드를 입력해주세요.",
        behavior: {
          required: false,
          triggers: [
            {
              type: TriggerType.Show,
              field: "useConversionCode",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
      {
        type: FormFieldType.Switch,
        id: "conversionCodeProductId",
        label: "전환코드 상품 아이디 ",
        // horizontal 레이어가 따로 없는 점 확인 필요
        layout: "horizontal",
        description: "상품 페이지 접근 시 전환 코드를 입력할 수 있습니다.",
        placeholder: "전환 코드를 입력해주세요.",
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useConversionCode",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Group,
    id: "customUtm",
    label: "UTM 태그",
    noneStyle: true,
    schemas: [
      {
        type: FormFieldType.Switch,
        id: "useCustomUtm",
        label: "UTM 태그 사용하기",
        layout: "horizontal",
      },
      {
        type: FormFieldType.Text,
        id: "campaignId",
        label: "캠페인 아이디",
        // horizontal 레이어가 따로 없는 점 확인 필요
        placeholder: "캠페인 아이디(ID)",
        description:
          "캠페인을 구분하기 위한 고유한 값을 입력해주세요.[mo] 예)1A45E",
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useCustomUtm",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
      {
        type: FormFieldType.Text,
        id: "campaignSource",
        label: "캠페인 소스",
        placeholder: "캠페인 소스(Source)",
        description:
          "유입 소스가 발생한 매체를 입력해주세요.[mo] 예)shortsworks",
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useCustomUtm",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
      {
        type: FormFieldType.Text,
        id: "campaignId",
        label: "캠페인 이름(Campaign)",
        description:
          "캠페인을 구분하기 위한 고유한 값을 입력해주세요.[mo] 예)1A45E",
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useCustomUtm",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
      {
        type: FormFieldType.Text,
        id: "campaignId",
        label: "캠페인 미디엄",
        layout: "horizontal",
        placeholder: "캠페인 미디엄(Medium)",
        description:
          "유입이 된 마케팅의 방법을 입력해주세요.[mo] 예)main_shorts",
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useCustomUtm",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
      {
        type: FormFieldType.Text,
        id: "campaignName",
        label: "캠페인 이름",
        layout: "horizontal",
        placeholder: "캠페인 이름(Campaign)",
        description:
          "유입이 진행 된 마케팅 캠페인이나 이벤트명을 입력해주세요.[mo] 예)summer_promotion",
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useCustomUtm",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
      {
        type: FormFieldType.Text,
        id: "campaignContent",
        label: "캠페인 콘텐츠",
        layout: "horizontal",
        placeholder: "캠페인 콘텐츠(Content)",
        description:
          "유입이 진행된 게시글이나 페이지를 입력해주세요.[mo] 예)detail_page",
        behavior: {
          triggers: [
            {
              type: TriggerType.Show,
              field: "useCustomUtm",
              value: true,
              condition: TriggerCondition.Equal,
            },
          ],
        },
      },
    ],
  },
];
